import React, {useEffect, useMemo, useState} from 'react';
import {observer} from 'mobx-react';
import {
    AppstoreOutlined,
    FileTextOutlined,
    InboxOutlined,
    ProfileOutlined,
    ScanOutlined,
    SearchOutlined,
    ShoppingCartOutlined,
    SwapOutlined,
} from '@ant-design/icons';
import {Text} from '../../styles/text';
import {Layout} from '../../styles/layout';
import {modalTextColor, primaryColor} from '../../utils/variables';
import ProductPicker from './ProductPicker';
import {
    getStoredProductPickerMode,
    normalizeProductPickerModes,
    setStoredProductPickerMode,
} from './modeStorage';
import * as S from './styles';
import {
    PRODUCT_PICKER_CONTEXT_META,
    PRODUCT_PICKER_CONTEXTS,
    PRODUCT_PICKER_MODE_META,
    PRODUCT_PICKER_MODES,
    ProductPickerContext,
    ProductPickerItem,
    ProductPickerMode,
    ProductPickResult,
} from './types';
import OwnerSettingsStore from '../../pages/owner.settings/owner.settings/owner.settings.store';

interface Props {
    /** Optional live products for preview; falls back to demo items */
    products?: ProductPickerItem[];
}

const DEMO_PRODUCTS: ProductPickerItem[] = [
    {
        id: 101,
        name: 'Смартфон Nova X',
        part_number: 'NVX-101',
        brand: 'Nova',
        category_id: 1,
        category: 'Смартфоны',
        last_price: 4500000,
        has_variation: true,
        variations_combinations: [
            {id: 1, display_name: '128GB / Чёрный', price: 4500000},
            {id: 2, display_name: '256GB / Синий', price: 5200000},
        ],
    },
    {
        id: 102,
        name: 'Чехол Soft Case',
        part_number: 'CASE-22',
        brand: 'Soft',
        category_id: 2,
        category: 'Аксессуары',
        last_price: 89000,
    },
    {
        id: 103,
        name: 'Наушники AirBeat',
        part_number: 'AB-9',
        brand: 'Beat',
        category_id: 2,
        category: 'Аксессуары',
        last_price: 320000,
    },
    {
        id: 104,
        name: 'Ноутбук ProBook 14',
        part_number: 'PB-14',
        brand: 'ProBook',
        category_id: 3,
        category: 'Ноутбуки',
        last_price: 12500000,
        has_variation: true,
        variations_combinations: [
            {id: 11, display_name: '16/512', price: 12500000},
            {id: 12, display_name: '32/1TB', price: 15800000},
        ],
    },
    {
        id: 105,
        name: 'Кабель USB-C 1м',
        part_number: 'CBL-1',
        brand: 'Wire',
        category_id: 2,
        category: 'Аксессуары',
        last_price: 45000,
    },
];

const CONTEXT_ICONS: Record<ProductPickerContext, React.ReactNode> = {
    orders: <FileTextOutlined />,
    operations: <ShoppingCartOutlined />,
    purchases: <InboxOutlined />,
    transfers: <SwapOutlined />,
};

const MODE_ICONS: Record<ProductPickerMode, React.ReactNode> = {
    quick_search: <SearchOutlined />,
    catalog_panel: <ProfileOutlined />,
    pos_scan: <ScanOutlined />,
    tiles_catalog: <AppstoreOutlined />,
};

const MODE_SHORT_TITLE: Record<ProductPickerMode, string> = {
    quick_search: 'Поиск',
    catalog_panel: 'Каталог',
    pos_scan: 'Сканер',
    tiles_catalog: 'Плитки',
};

const ProductPickerSettings = observer(({products}: Props) => {
    const {ownerSettings, setOwnerSettingsForm} = OwnerSettingsStore;
    const [activeContext, setActiveContext] = useState<ProductPickerContext>('orders');
    const [modes, setModes] = useState(() =>
        normalizeProductPickerModes(
            ownerSettings.product_picker_modes,
            ownerSettings.product_picker_mode,
        ),
    );
    const [lastPick, setLastPick] = useState<string>('');

    useEffect(() => {
        setModes(
            normalizeProductPickerModes(
                ownerSettings.product_picker_modes,
                ownerSettings.product_picker_mode,
            ),
        );
    }, [ownerSettings.product_picker_mode, ownerSettings.product_picker_modes]);

    const previewProducts = useMemo(
        () => (products && products.length > 0 ? products.slice(0, 40) : DEMO_PRODUCTS),
        [products],
    );

    const activeMode = modes[activeContext] || getStoredProductPickerMode(activeContext);
    const activeModeMeta = PRODUCT_PICKER_MODE_META[activeMode];
    const activeContextMeta = PRODUCT_PICKER_CONTEXT_META[activeContext];

    const handleModeChange = (next: ProductPickerMode) => {
        const nextModes = {
            ...modes,
            [activeContext]: next,
        };
        setModes(nextModes);
        setStoredProductPickerMode(next, activeContext);
        setOwnerSettingsForm({
            ...ownerSettings,
            product_picker_modes: nextModes,
            product_picker_mode: nextModes.orders,
        });
        setLastPick('');
    };

    const handlePick = (result: ProductPickResult) => {
        const variation = result.variation?.display_name
            ? ` / ${result.variation.display_name}`
            : '';
        setLastPick(`${result.product.name}${variation}`);
    };

    return (
        <Layout vertical between={16}>
            <Text color={modalTextColor} extent="S">
                Сначала выберите страницу, затем вид интерфейса. Настройки сохраняются для всей организации.
            </Text>

            <S.SettingsSection>
                <S.SettingsLabel>Страница</S.SettingsLabel>
                <S.PageTabs>
                    {PRODUCT_PICKER_CONTEXTS.map((context) => {
                        const meta = PRODUCT_PICKER_CONTEXT_META[context];
                        const modeForContext = modes[context];
                        return (
                            <S.PageTab
                                key={context}
                                type="button"
                                $active={activeContext === context}
                                onClick={() => {
                                    setActiveContext(context);
                                    setLastPick('');
                                }}
                            >
                                {CONTEXT_ICONS[context]}
                                {meta.title}
                                <S.PageTabMeta>
                                    {MODE_SHORT_TITLE[modeForContext]}
                                </S.PageTabMeta>
                            </S.PageTab>
                        );
                    })}
                </S.PageTabs>
            </S.SettingsSection>

            <S.SettingsSection>
                <S.SettingsLabel>Вид для «{activeContextMeta.title}»</S.SettingsLabel>
                <S.ModeSetupGrid>
                    <S.ModeChipList>
                        {PRODUCT_PICKER_MODES.map((mode) => {
                            const active = activeMode === mode;
                            return (
                                <S.ModeChip
                                    key={mode}
                                    type="button"
                                    $active={active}
                                    onClick={() => handleModeChange(mode)}
                                >
                                    <S.ModeChipIcon $active={active}>
                                        {MODE_ICONS[mode]}
                                    </S.ModeChipIcon>
                                    <S.ModeChipTitle>
                                        {MODE_SHORT_TITLE[mode]}
                                    </S.ModeChipTitle>
                                </S.ModeChip>
                            );
                        })}
                    </S.ModeChipList>

                    <S.ModeDetailsPanel>
                        <Layout vertical between={8}>
                            <S.ModeDetailsTitle>
                                {activeModeMeta.title}
                            </S.ModeDetailsTitle>
                            <S.ModeDetailsText>
                                {activeModeMeta.description}
                            </S.ModeDetailsText>
                            <S.ModeDetailsText>
                                Подходит для: {activeModeMeta.bestFor}
                            </S.ModeDetailsText>
                        </Layout>

                        <S.PreviewFrame style={{marginTop: 0}}>
                            <Layout vertical between={10}>
                                <Text extent="S" weight="bold" color={modalTextColor}>
                                    Превью
                                </Text>
                                <ProductPicker
                                    mode={activeMode}
                                    context={activeContext}
                                    products={previewProducts}
                                    onPick={handlePick}
                                />
                                {lastPick && (
                                    <Text extent="S" color={primaryColor}>
                                        Выбрано: {lastPick}
                                    </Text>
                                )}
                            </Layout>
                        </S.PreviewFrame>
                    </S.ModeDetailsPanel>
                </S.ModeSetupGrid>
            </S.SettingsSection>
        </Layout>
    );
});

export default ProductPickerSettings;
